'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="The Recycled Bottle Is the More Contaminated One"
      author="MicroPlastics Research Desk"
      date="August 23, 2026"
      readTime="12 min read"
      heroImage="/blog/heroes/general.webp"
      heroImageAlt="Recycled PET bottles carry higher reported migration of antimony and bisphenol A than virgin PET, because recycling concentrates contamination from the previous life of the plastic"
    >
      <BlogLastUpdated iso="2026-08-23" formatted="August 23, 2026" />

      <BlogTLDR>
        <strong>For food contact specifically, recycled PET is the version with
        more reported chemical migration — not less.</strong>{' '}
        A Brunel University evidence map published in the{' '}
        <em>Journal of Hazardous Materials</em> traced{' '}
        <strong>193 food-contact chemicals</strong> across the life of a PET
        drink bottle and found <strong>150 of them documented migrating</strong>{' '}
        into food or food simulants. Its finding on recycling is stated plainly:{' '}
        <strong>higher migration of antimony and bisphenol A has been reported
        in recycled PET than in virgin PET.</strong> The reason is mechanical
        rather than sinister. A recycled bottle inherits whatever its previous
        life absorbed, plus whatever survived collection and sorting, plus
        another round of heat — and the authors attribute the gap to exactly
        that: multiple contamination sources and variable decontamination.{' '}
        <strong>None of this is an argument against recycling.</strong> It is an
        argument that &ldquo;100% recycled&rdquo; on the front of a bottle is a
        claim about waste, not about what is in your drink — and it is now
        printed on an enormous amount of packaging. The same authors are careful
        to add that the safety implications of PET recycling{' '}
        <strong>remain underexplored</strong>, which is its own kind of answer.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>150 of 193 mapped food-contact chemicals</strong> have been documented migrating from PET drink bottles into food or food simulants.</>,
          <><strong>Antimony and BPA migrate more from rPET than virgin PET</strong>, according to the studies pooled in the Brunel evidence map.</>,
          <><strong>The cause is accumulation, not additives.</strong> Contamination enters through prior use, collection, sorting and imperfect decontamination — each cycle adds opportunities.</>,
          <><strong>Antimony is not a contaminant — it is the catalyst.</strong> Antimony trioxide is used to make PET in the first place, which is why it is the chemical most studied here.</>,
          <><strong>Heat is the multiplier.</strong> Migration rises with temperature and storage time, so a recycled bottle left warm is the worst case of a worst case.</>,
          <><strong>&ldquo;Food-grade recycled&rdquo; is a real regulatory category</strong>, not marketing — recycling processes must be individually assessed for food contact. It is also why not all rPET is equivalent.</>,
          <><strong>The evidence base is thin, and the authors say so.</strong> They describe the safety implications of PET recycling as underexplored, which is a reason for caution in both directions.</>,
          <><strong>Recycling is still worth doing.</strong> This is a food-contact finding, not a waste-policy one. The practical response is about what you drink from, not what you put in the kerbside bin.</>,
        ]}
      />

      <KeyStats
        heading="The numbers, and where each one comes from"
        stats={[
          { value: '150 of 193', label: 'food-contact chemicals documented migrating from PET bottles', context: 'mapped across the full bottle lifecycle into food or food simulants', source: 'Gerassimidou et al., Journal of Hazardous Materials, 2022', sourceUrl: 'https://pubmed.ncbi.nlm.nih.gov/35295000/' },
          { value: 'Higher', label: 'antimony and BPA migration in recycled vs virgin PET', context: 'attributed to multiple contamination sources and variability in collection, sorting and decontamination efficiency', source: 'Gerassimidou et al., 2022', sourceUrl: 'https://pubmed.ncbi.nlm.nih.gov/35295000/' },
          { value: 'Underexplored', label: 'the authors’ own verdict on rPET safety evidence', context: 'a statement about how little is known, not a clearance — and the reason this page avoids confident numbers', source: 'Gerassimidou et al., 2022', sourceUrl: 'https://pubmed.ncbi.nlm.nih.gov/35295000/' },
          { value: '50%', label: 'certified recycled content in NutriBullet’s Tritan Renew cups', context: 'one example of how normal recycled-content claims have become on food-contact plastic', source: 'NutriBullet (manufacturer)', sourceUrl: 'https://www.nutribullet.com/sustainability/' },
        ]}
      />

      <h2>The short answer</h2>
      <p>
        If you have been choosing the bottle that says &ldquo;made from 100%
        recycled plastic&rdquo; on the assumption that it is the cleaner option
        to drink from, the evidence does not support that assumption. It
        supports the opposite, weakly, and mostly for two specific chemicals.
      </p>
      <p>
        The logic is not complicated once you see it. Virgin PET starts as
        polymer and additives and nothing else. Recycled PET starts as bottles
        that held something, sat somewhere, were collected with other things,
        were sorted imperfectly, and were then melted again. Every one of those
        steps is a chance to pick something up, and none of them is a chance to
        put something back to new.
      </p>

      <h2>What the research actually is</h2>
      <p>
        The central source here is Gerassimidou and colleagues at Brunel
        University London, published in the{' '}
        <em>Journal of Hazardous Materials</em> in March 2022. It is worth being
        precise about what kind of study it is, because much of the coverage is
        not.
      </p>
      <p>
        <strong>It is a systematic evidence map, not a laboratory test.</strong>{' '}
        The team did not buy bottles and measure them. They gathered what the
        published literature already reported about food-contact chemicals
        across the entire PET bottle value chain and organised it — which makes
        it good for seeing the shape of the field, and unsuitable for quoting as
        &ldquo;a study that tested bottles and found X&rdquo;.
      </p>
      <p>
        What they mapped: <strong>193 food-contact chemicals</strong>{' '}
        investigated across the lifecycle, of which{' '}
        <strong>150 have been detected migrating</strong> from PET bottles into
        food simulants or food samples. Most of the research attention has gone
        to antimony, acetaldehyde and a set of well-known endocrine-disrupting
        compounds.
      </p>
      <p>
        And on recycling, the sentence that matters:{' '}
        <strong>&ldquo;the higher migration of Sb and Bishphenol A has been
        reported in recycled (rPET) compared to virgin PET&rdquo;</strong> —
        which they attribute to &ldquo;multiple contamination sources and the
        variability in the collection, sorting, and decontamination
        efficiency&rdquo;.
      </p>
      <p>
        One widely repeated line does <em>not</em> come from this paper: the
        claim that 18 chemicals were found exceeding health regulations. That
        appears in news coverage; we could not verify it against the primary
        source, so it is deliberately absent from this page.
      </p>

      <h3>Why antimony keeps coming up</h3>
      <p>
        Antimony is not something that falls into recycled plastic by accident.
        <strong>Antimony trioxide is the catalyst used to manufacture PET in the
        first place</strong>, which means it is present in essentially all PET,
        virgin included. That is precisely why it is the most-studied chemical
        in this literature — it is always there, so it is the natural thing to
        measure, and differences between virgin and recycled material show up
        against it clearly.
      </p>
      <p>
        Its behaviour is also the familiar one: migration increases with{' '}
        <strong>heat and storage time</strong>. Which is the same mechanism
        behind{' '}
        <Link href="/blog/water-bottle-left-in-hot-car-microplastics" className="text-forest underline">what happens to a bottle left in a hot car</Link>,
        and it stacks with everything above.
      </p>

      <h2>Why recycled plastic accumulates what virgin does not</h2>
      <p>
        There are four distinct entry points, and they are cumulative:
      </p>
      <ul>
        <li>
          <strong>The previous contents.</strong> A bottle that held a flavoured
          or oily product can absorb components of it into the polymer. Those do
          not necessarily leave during reprocessing.
        </li>
        <li>
          <strong>Non-food-grade material in the stream.</strong> Collection is
          imperfect, and PET that never held food — packaging, textiles, trays —
          can enter a bale destined for bottles.
        </li>
        <li>
          <strong>Misuse before disposal.</strong> Bottles are widely reused as
          containers for household chemicals, fuels and solvents before being
          thrown out. Sorting cannot reliably detect that.
        </li>
        <li>
          <strong>Another thermal cycle.</strong> Reprocessing means melting
          again. PET degrades with heat, and each cycle shortens chains and
          generates breakdown products, which is why recycled material is often
          blended with virgin rather than used alone.
        </li>
      </ul>
      <p>
        Decontamination processes exist and work — that is the entire point of
        &ldquo;food-grade&rdquo; recycling. But the authors&rsquo; phrase is{' '}
        <em>variability in decontamination efficiency</em>. The process is real;
        its consistency is the open question.
      </p>

      <BlogComparisonTable
        caption="Virgin PET vs recycled PET, for food contact specifically (checked August 2026)"
        columns={[
          { key: 'f', label: 'Factor' },
          { key: 'v', label: 'Virgin PET' },
          { key: 'r', label: 'Recycled PET' },
        ]}
        rows={[
          { f: 'Antimony', v: 'Present — it is the manufacturing catalyst', r: 'Higher migration reported' },
          { f: 'Bisphenol A', v: 'Not intentionally used in PET', r: 'Higher migration reported' },
          { f: 'Contamination entry points', v: 'Manufacturing only', r: 'Prior contents, sorting, misuse, reprocessing' },
          { f: 'Thermal history', v: 'One melt cycle', r: 'Two or more — degradation is cumulative' },
          { f: 'Environmental case', v: 'Weakest — new fossil feedstock', r: 'Strongest, and genuinely so' },
          { f: 'What the label tells you', v: 'Nothing about migration', r: 'Nothing about migration' },
        ]}
      />

      <BlogAppCTA
        headline="Not sure what your bottle or container is actually made of?"
        sub="Scan it for the polymer, a 0–100 risk score, and a lower-plastic swap in about five seconds."
      />

      <h2>The part that makes this awkward</h2>
      <p>
        Recycled content is not a niche claim any more. It is becoming the
        default on food-contact plastic, driven by genuine and reasonable
        pressure to cut virgin production. Brands advertise it prominently
        because consumers reward it — NutriBullet, for one, moved its blending
        cups to{' '}
        <strong>Tritan Renew, made with 50% certified recycled content</strong>,
        and says so on its own sustainability page.
      </p>
      <p>
        That creates a genuine tension rather than a villain. The environmental
        case for recycled content is strong and this page does not dispute it.
        The food-contact case is the one that has been assumed rather than
        demonstrated, and those are different questions that the same three
        words on a label are currently answering at once.
      </p>
      <p>
        It is also the fourth time this pattern has appeared on this site. {' '}
        <Link href="/blog/is-bpa-free-plastic-safe" className="text-forest underline">&ldquo;BPA-free&rdquo;</Link>{' '}
        described one molecule&rsquo;s absence, not safety.{' '}
        <Link href="/blog/what-does-microwave-safe-actually-mean" className="text-forest underline">&ldquo;Microwave safe&rdquo;</Link>{' '}
        described a container not melting, not particles. {' '}
        <Link href="/blog/is-tritan-a-microplastic" className="text-forest underline">Tritan</Link>{' '}
        is genuinely bisphenol-free and still abrades at the cap. Now
        &ldquo;100% recycled&rdquo; describes where the material came from.
        Every one of those claims is true. None of them answers the question the
        shopper is actually asking.
      </p>

      <h2>What we still do not know</h2>
      <ul>
        <li>
          <strong>How large the difference is.</strong> The finding is
          directional — higher migration reported — not quantified into a
          reliable ratio. Anyone giving you a precise multiple is going beyond
          the evidence.
        </li>
        <li>
          <strong>Whether it matters at the doses involved.</strong> Detecting
          migration is not the same as demonstrating harm at real exposures, and
          the paper does not make that claim.
        </li>
        <li>
          <strong>Whether current processes have closed the gap.</strong> The
          map was published in 2022 and draws on earlier literature.
          Decontamination technology has not stood still.
        </li>
        <li>
          <strong>Almost everything, by the authors&rsquo; own account.</strong>{' '}
          They describe the safety implications of PET recycling as{' '}
          <em>underexplored</em>. That cuts both ways, and it is the honest
          summary of the field.
        </li>
      </ul>

      <h2>What to actually do</h2>
      <p>
        Nothing here justifies a panic, and none of it changes the case for
        recycling as waste policy. What it changes is which signals you trust
        when the material will be touching your food:
      </p>
      <ul>
        <li>
          <strong>Stop reading &ldquo;recycled&rdquo; as &ldquo;cleaner to drink
          from&rdquo;.</strong> Read it as what it is — a claim about feedstock
          and waste, which is a good thing that is answering a different
          question.
        </li>
        <li>
          <strong>Heat is still the biggest lever you control.</strong> Migration
          rises with temperature and time regardless of virgin or recycled, so
          not leaving bottles in cars, sun or warm cupboards does more than
          choosing between the two.
        </li>
        <li>
          <strong>For water you drink daily, the material question beats the
          sourcing question.</strong>{' '}
          <Link href="/blog/best-glass-water-bottles" className="text-forest underline">Glass</Link>{' '}
          or{' '}
          <Link href="/blog/best-stainless-steel-water-bottles" className="text-forest underline">stainless steel</Link>{' '}
          sidesteps the entire virgin-versus-recycled debate, because neither is
          PET.
        </li>
        <li>
          <strong>Do not reuse a drinks bottle for anything but drinks</strong>,
          and do not store chemicals in one before recycling it. That is one of
          the documented contamination routes, and it is the only one an
          individual actually controls.
        </li>
        <li>
          <strong>Keep recycling.</strong> The response to this finding is
          better decontamination standards and more research, not less
          collection. Treating it as an argument against kerbside recycling
          would be the wrong lesson entirely.
        </li>
      </ul>

      <p>
        Related reading:{' '}
        <Link href="/blog/plastic-recycling-numbers-microplastics" className="text-forest underline">what the recycling numbers actually mean</Link>,{' '}
        <Link href="/blog/is-bpa-free-plastic-safe" className="text-forest underline">is BPA-free plastic safe</Link>,{' '}
        <Link href="/blog/is-tritan-a-microplastic" className="text-forest underline">is Tritan a microplastic</Link>,{' '}
        <Link href="/blog/microplastics-bottled-water-brands-ranked" className="text-forest underline">bottled water brands ranked</Link>, and{' '}
        <Link href="/blog/water-bottle-left-in-hot-car-microplastics" className="text-forest underline">bottles left in a hot car</Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Is recycled plastic safer than virgin plastic?',
            answer: 'For food contact, the evidence points the other way. A Brunel University evidence map published in the Journal of Hazardous Materials in 2022 reported higher migration of antimony and bisphenol A from recycled PET than from virgin PET, attributing it to multiple contamination sources and variability in collection, sorting and decontamination. The environmental case for recycled content is strong and separate; the food-contact case has been assumed rather than demonstrated.',
          },
          {
            question: 'Why would recycled plastic be more contaminated?',
            answer: 'Because it accumulates. A recycled bottle can carry residues absorbed from whatever it previously held, material that entered the stream from non-food-grade PET, residues from bottles reused for household chemicals before disposal, and breakdown products from an additional melt cycle. Virgin PET has one entry point — manufacturing. Decontamination processes exist and work, but the authors specifically flag variability in how well they work.',
          },
          {
            question: 'What is antimony and why is it in PET bottles?',
            answer: 'Antimony trioxide is the catalyst used to manufacture PET, so antimony is present in essentially all PET including virgin material. That is why it is the most-studied chemical in this field: it is always there, which makes it the natural benchmark for comparing materials. Its migration into contents increases with heat and storage time, which is the same mechanism behind advice not to leave bottles in hot cars.',
          },
          {
            question: 'Does "food-grade recycled" mean it is safe?',
            answer: 'It means the recycling process has been assessed for food-contact use, which is a real regulatory category rather than marketing, and it is why not all recycled PET is equivalent. What it does not mean is that migration has been shown to match virgin material. The 2022 evidence map describes the safety implications of PET recycling as underexplored — a statement about how little is settled, rather than a clearance or a condemnation.',
          },
          {
            question: 'Should I stop buying products with recycled plastic?',
            answer: 'No, and this finding is not an argument against recycling. It is an argument against reading "100% recycled" as a health claim, because it is a claim about feedstock and waste. If you want to remove the question entirely for something you drink from daily, glass or stainless steel is not PET at all. For everything else, controlling heat and storage time does more than choosing between virgin and recycled.',
          },
          {
            question: 'How much more contamination is in recycled PET?',
            answer: 'Nobody can honestly tell you. The finding is directional — higher migration reported for antimony and BPA — and was not quantified into a reliable ratio, because it pools results across studies with different methods and materials rather than measuring bottles directly. Any specific multiple you see quoted has gone beyond what the source supports.',
          },
          {
            question: 'Is the Brunel study a test of actual bottles?',
            answer: 'No, and this is commonly misreported. It is a systematic evidence map: the researchers gathered and organised what the published literature already reported about food-contact chemicals across the PET bottle value chain. That makes it strong for showing the shape of the field — 150 of 193 mapped chemicals documented migrating — and unsuitable for quoting as a direct measurement of any particular bottle.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Gerassimidou S, Lanska P, Hahladakis JN, Lovat E, Vanzetto S, Geueke B, Groh KJ, Muncke J, Maffini M, Martin OV, Iacovidou E', year: 2022, title: 'Unpacking the complexity of the PET drink bottles value chain: a chemicals perspective — systematic evidence map of 193 food-contact chemicals, 150 detected migrating into food or food simulants; "the higher migration of Sb and Bishphenol A has been reported in recycled (rPET) compared to virgin PET"; safety implications of PET recycling described as underexplored', publisher: 'Journal of Hazardous Materials 430:128410', url: 'https://pubmed.ncbi.nlm.nih.gov/35295000/' },
          { authors: 'NutriBullet', year: 2026, title: 'Tritan Renew sustainable blending cups — blending cups made with 50% certified recycled content, an example of how standard recycled-content claims have become on food-contact plastic', publisher: 'NutriBullet (manufacturer)', url: 'https://www.nutribullet.com/sustainability/' },
        ]}
      />
    </BlogPostLayout>
  )
}
